export type CacheItem<T> = {
  value: T;
  timestamp: number;
};

export type CacheOptions = {
  maxSize?: number;
  ttlMinutes?: number;
};

export type Cache<T> = {
  [key: string]: CacheItem<T>;
};

export const createCache = <T>(options: CacheOptions = {}) => {
  const maxSize = options.maxSize || 100;
  const ttl = (options.ttlMinutes || 30) * 60 * 1000; // 30분을 ms로 변환
  let cache: Cache<T> = {};

  const set = (key: string, value: T) => {
    const keys = Object.keys(cache);
    if (keys.length >= maxSize) {
      // 가장 오래된 항목 제거
      const oldestKey = keys.reduce((oldest, current) =>
        cache[oldest].timestamp < cache[current].timestamp ? oldest : current
      );
      delete cache[oldestKey];
    }

    cache[key] = {
      value,
      timestamp: Date.now(),
    };
  };

  const get = (key: string): T | null => {
    const item = cache[key];
    if (!item) return null;

    // TTL 체크
    if (Date.now() - item.timestamp > ttl) {
      delete cache[key];
      return null;
    }

    // 타임스탬프 갱신
    cache[key] = {
      ...item,
      timestamp: Date.now(),
    };

    return item.value;
  };

  const clear = () => {
    cache = {};
  };

  const removeExpired = () => {
    const now = Date.now();
    Object.keys(cache).forEach((key) => {
      if (now - cache[key].timestamp > ttl) {
        delete cache[key];
      }
    });
  };

  return {
    set,
    get,
    clear,
    removeExpired,
  };
};
